import { PHOTOS } from '../../data/photos';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Globe, Headset, GitMerge, Cpu, HardDrive, Network, RefreshCw, Lock, Wrench, Briefcase, Building2, Zap, Server, Users } from 'lucide-react';
import { Reveal } from '../../components/shared/Reveal';
import { products } from '../../data/products';
import { solutionCategories } from '../../data/solutionCategories';

/* ── Proof points ── */
const TRUST_ITEMS = [
  { icon: Shield, title: 'Secure by design', desc: 'Encryption, segmentation and monitoring are defaults, not add-ons.' },
  { icon: Globe, title: 'Data sovereignty', desc: 'Control where workloads run and where data is stored.' },
  { icon: Headset, title: 'Enterprise support', desc: 'Engineers in-region, accountable response times.' },
  { icon: GitMerge, title: 'Hybrid-ready', desc: 'Connect on-premises facilities to DAAKYI Cloud privately.' }
];

export const TrustSection = () => (
  <section data-testid="trust-section" className="bg-white border-b border-[#DCE4EE]">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-12">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {TRUST_ITEMS.map(({ icon: Icon, title, desc }) => (
          <div key={title} className="flex gap-3.5">
            <Icon className="w-5 h-5 text-[#A8631A] shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="text-[#1A1240] text-sm font-semibold mb-1">{title}</p>
              <p className="text-[#64748B] text-sm leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ── Core Products ── */
const PRODUCT_ICONS = {
  compute: Cpu, storage: HardDrive, networking: Network,
  'cloud-security': Lock, 'backup-dr': RefreshCw, 'managed-cloud': Wrench
};

export const ProductsSection = () => (
  <section data-testid="products-section" className="bg-white py-20 lg:py-28">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <Reveal>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div className="max-w-[720px]">
            <p className="text-[#A8631A] text-xs font-semibold uppercase tracking-widest mb-4">Core Products</p>
            <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-[#1A1240]">
              Everything you need to run, secure and recover.
            </h2>
          </div>
          <Link to="/products" data-testid="home-view-all-products" className="text-[#A8631A] hover:text-[#DD8226] text-sm font-semibold inline-flex items-center gap-1.5 shrink-0 transition-colors">
            View all products <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {products.map((p) => {
          const Icon = PRODUCT_ICONS[p.id] || Cpu;
          return (
            <Reveal key={p.id}>
              <div className="h-full border border-[#DCE4EE] rounded-xl p-6 bg-white hover:border-[#B9C8DD] hover:shadow-[0_8px_24px_rgba(15,23,42,0.06)] transition-all duration-200">
                <Icon className="w-6 h-6 text-[#A8631A] mb-4" strokeWidth={1.5} />
                <h3 className="text-[#1A1240] text-lg font-bold mb-2">{p.name}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed mb-4">{p.description}</p>
                <Link to={`/products/${p.id}`} data-testid={`explore-${p.id}`}
                  className="inline-flex items-center gap-1.5 text-[#A8631A] hover:text-[#DD8226] text-sm font-semibold transition-colors">
                  Explore {p.shortName} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

/* ── Secure by Default ── */
const SECURITY_LAYERS = [
  { label: 'Users and Workloads', tone: 'neutral' },
  { label: 'Identity and Access Management', tone: 'blue' },
  { label: 'Zero Trust Controls', tone: 'blue' },
  { label: 'DAAKYI Cloud Infrastructure', tone: 'cyan' },
  { label: 'Cloud Workloads and Data', tone: 'teal' },
  { label: 'SOC, XDR and Threat Monitoring', tone: 'teal' }
];

const LAYER_STYLES = {
  neutral: 'border-white/15 text-[#CBD5E1]',
  blue: 'border-[#F29B3D]/40 text-[#F1F5F9]',
  cyan: 'border-[#F29B3D]/60 text-[#F1F5F9]',
  teal: 'border-[#F29B3D]/80 text-[#F6C68C]'
};

const SECURITY_CAPABILITIES = [
  'Identity and access management', 'Encryption in transit and at rest', 'Network segmentation',
  'Firewall protection', 'Threat detection', 'Security monitoring', 'Vulnerability management',
  'Backup and recovery', 'Incident response', 'Security standards support'
];

export const SecuritySection = () => (
  <section data-testid="security-section" className="section-dark py-20 lg:py-28 border-y border-white/[0.05]">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <p className="text-[#F29B3D] text-xs font-semibold uppercase tracking-widest mb-4">Secure by Default</p>
          <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight mb-5">
            Security is not an add-on.<br />It is built into the cloud.
          </h2>
          <p className="text-[#93A5BC] text-[15px] leading-relaxed max-w-[480px] mb-8">
            Every workload on DAAKYI Cloud passes through layered controls — from identity
            verification to continuous monitoring by our security operations capability.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mb-8">
            {SECURITY_CAPABILITIES.map(c => (
              <li key={c} className="flex items-center gap-2.5 text-sm text-[#CBD5E1]">
                <span className="w-1 h-1 rounded-full bg-[#F29B3D] shrink-0" /> {c}
              </li>
            ))}
          </ul>
          <Link to="/security" data-testid="security-learn-more" className="inline-flex items-center gap-2 text-[#F29B3D] hover:text-white text-sm font-semibold transition-colors">
            Explore the security model <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Architecture diagram */}
        <div className="lg:col-span-6 lg:pl-8">
          <div className="card-dark p-6 sm:p-8" data-testid="security-diagram">
            <p className="text-[#94A3B8] text-[11px] uppercase tracking-widest mb-5">Security architecture</p>
            <div className="flex flex-col items-stretch">
              {SECURITY_LAYERS.map((layer, i) => (
                <div key={layer.label} className="flex flex-col items-center">
                  <div className={`w-full text-center border rounded-lg py-3 px-4 text-sm font-medium bg-white/[0.02] ${LAYER_STYLES[layer.tone]}`}>
                    {layer.label}
                  </div>
                  {i < SECURITY_LAYERS.length - 1 && (
                    <svg width="2" height="18" className="my-0.5" aria-hidden="true">
                      <line x1="1" y1="0" x2="1" y2="18" stroke="rgba(242,155,61,0.55)" strokeWidth="2" className="flow-line" />
                    </svg>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── Managed Cloud ── */
const MANAGED_POINTS = [
  ['Managed migration', 'DAAKYI engineers plan and execute moves off VMware and physical servers.'],
  ['Day-2 operations', 'Patching, capacity and incident response handled against agreed service levels.'],
  ['Dedicated environments', 'Private, physically isolated stacks operated by DAAKYI — in-region or on-premises.'],
  ['In-region support', 'Engineers in your time zone with accountable response times.']
];

export const ManagedCloudSection = () => (
  <section data-testid="managed-cloud-section" className="bg-[#F8FAFC] py-20 lg:py-28">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="gold-detail text-xs font-semibold uppercase tracking-widest mb-4 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F29B3D]" /> Managed Cloud
          </p>
          <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-[#1A1240] mb-5">
            Your cloud, run by our engineers.
          </h2>
          <p className="text-[#64748B] text-[15px] leading-relaxed max-w-[440px] mb-8">
            Not every organization wants to build a cloud operations team. DAAKYI designs,
            migrates, operates and secures your environment end to end.
          </p>
          <Link to="/products/managed-cloud" data-testid="managed-cloud-link" className="inline-flex items-center gap-2 text-[#A8631A] hover:text-[#DD8226] text-sm font-semibold transition-colors">
            Explore Managed Cloud <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
            {MANAGED_POINTS.map(([title, desc]) => (
              <div key={title} className="border-t border-[#C9D6E6] pt-4">
                <p className="text-[#1A1240] text-sm font-semibold mb-1">{title}</p>
                <p className="text-[#64748B] text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── Solutions Preview ── */
const CATEGORY_ICONS = {
  'sovereign-cloud': Globe, enterprise: Briefcase, government: Building2,
  'critical-infrastructure': Zap, msps: Server, 'smes-startups': Users
};

export const SolutionsPreviewSection = () => (
  <section data-testid="solutions-preview-section" className="bg-white py-20 lg:py-28">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <p className="text-[#A8631A] text-xs font-semibold uppercase tracking-widest mb-4">Solutions</p>
          <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-[#1A1240] mb-5">
            Built around how you operate.
          </h2>
          <p className="text-[#64748B] text-[15px] leading-relaxed mb-6">
            From sovereign hosting to fully managed environments — with deep guidance for
            regulated industries like finance, healthcare and government.
          </p>
          <Link to="/solutions" data-testid="home-view-all-solutions" className="inline-flex items-center gap-2 text-[#A8631A] hover:text-[#DD8226] text-sm font-semibold transition-colors">
            Explore all solutions <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="lg:col-span-8">
          <div className="divide-y divide-[#DCE4EE] border-y border-[#DCE4EE]">
            {solutionCategories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat.id] || Globe;
              return (
                <Link key={cat.id} to={cat.href || `/solutions#${cat.id}`} data-testid={`home-solution-${cat.id}`}
                  className="group flex items-center justify-between py-5 px-2 hover:bg-[#F7F9FC] transition-colors">
                  <div className="flex items-center gap-4 min-w-0">
                    <Icon className="w-5 h-5 text-[#A8631A] shrink-0" strokeWidth={1.5} />
                    <div className="min-w-0">
                      <p className="text-[#1A1240] font-semibold text-[15px] group-hover:text-[#A8631A] transition-colors">{cat.name}</p>
                      <p className="text-[#64748B] text-sm mt-0.5 line-clamp-1">{cat.audience}</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#94A3B8] group-hover:text-[#A8631A] group-hover:translate-x-1 transition-all shrink-0 ml-6" />
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── Pricing (controlled) ── */
export const PricingFrameworkSection = () => (
  <section data-testid="pricing-framework-section" className="bg-[#F8FAFC] py-20 lg:py-24">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <p className="text-[#A8631A] text-xs font-semibold uppercase tracking-widest mb-4">Pricing</p>
          <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-[#1A1240] mb-4">
            Pricing configured to your workloads.
          </h2>
          <p className="text-[#64748B] text-[15px] leading-relaxed max-w-[560px]">
            Flexible compute, storage, networking, security and managed cloud configurations
            based on workload requirements. Standardized public packages are being finalized —
            billing currency and commercial terms are confirmed during scoping.
          </p>
        </div>
        <div className="lg:col-span-5 flex flex-col sm:flex-row lg:flex-col gap-3">
          <Link to="/contact" data-testid="pricing-request" className="btn-primary text-center">Request Pricing</Link>
          <Link to="/pricing" data-testid="pricing-view" className="btn-glass text-center">How Pricing Works</Link>
          <Link to="/contact" data-testid="pricing-architect" className="text-[#A8631A] hover:text-[#DD8226] text-sm font-semibold text-center py-2 transition-colors">
            Talk to a Cloud Architect →
          </Link>
        </div>
      </div>
    </div>
  </section>
);

/* ── Developer platform ── */
const TERMINAL_LINES = [
  { p: '$', c: 'curl -X POST api.daakyicloud.com/v1/auth/tokens -d \'{"api_key": "..."}\'' },
  { p: '', c: '✓ token issued · role: operator · expires in 3600s', dim: true },
  { p: '$', c: 'curl -X POST api.daakyicloud.com/v1/vms -d \'{"name": "app-01", "vcpus": 4, ...}\'' },
  { p: '', c: '✓ vm-1b4c9d0e provisioning · 4 vCPU · 8 GB · accra-1', dim: true },
  { p: '$', c: 'curl -X POST api.daakyicloud.com/v1/vms/vm-1b4c9d0e/backups' },
  { p: '', c: '✓ backup started · policy-independent restore point', dim: true }
];

export const DeveloperSection = () => (
  <section data-testid="developer-section" className="section-dark py-20 lg:py-28 border-y border-white/[0.05]">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <p className="text-[#F29B3D] text-xs font-semibold uppercase tracking-widest mb-4">Developer Platform</p>
          <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight mb-5">
            Built for engineers, documented like it matters.
          </h2>
          <p className="text-[#93A5BC] text-[15px] leading-relaxed mb-8 max-w-[440px]">
            Open REST APIs with scoped keys and a full audit trail. Provision VMs, storage and
            networks from your pipeline, with migration and operations guides written by practitioners.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <Link to="/docs/getting-started/introduction" data-testid="dev-docs-link" className="text-[#F29B3D] hover:text-white text-sm font-semibold transition-colors">Documentation →</Link>
            <Link to="/docs/api-reference/authentication" data-testid="dev-api-link" className="text-[#F29B3D] hover:text-white text-sm font-semibold transition-colors">API Reference →</Link>
            <Link to="/developers" data-testid="dev-hub-link" className="text-[#F29B3D] hover:text-white text-sm font-semibold transition-colors">Developer Hub →</Link>
          </div>
        </div>
        <div className="lg:col-span-7">
          <div className="card-dark overflow-hidden" data-testid="dev-terminal">
            <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/[0.06]">
              <span className="w-2.5 h-2.5 rounded-full bg-white/10" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/10" />
              <span className="w-2.5 h-2.5 rounded-full bg-white/10" />
              <span className="text-[#94A3B8] text-[11px] ml-2 font-mono">api.daakyicloud.com</span>
            </div>
            <div className="p-5 font-mono text-[13px] leading-7">
              {TERMINAL_LINES.map((l, i) => (
                <div key={`tl-${i}`} className={l.dim ? 'text-[#F6C68C]/85' : 'text-[#CBD5E1]'}>
                  {l.p && <span className="text-[#F29B3D] mr-2">{l.p}</span>}{l.c}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ── Why DAAKYI Cloud ── */
const WHY_PILLARS = [
  { icon: Globe, title: 'Africa-First Infrastructure', desc: 'Cloud infrastructure designed around the operational requirements of African organizations.' },
  { icon: Shield, title: 'Secure by Default', desc: 'Security controls integrated throughout infrastructure, networking and workload deployment.' },
  { icon: Users, title: 'Locally Operated', desc: 'Cloud infrastructure supported by DAAKYI\u2019s cloud and cybersecurity teams.' },
  { icon: Wrench, title: 'Managed When You Need It', desc: 'Consume infrastructure directly, or engage DAAKYI to help operate and secure your environment.' }
];

export const WhyDaakyiSection = () => (
  <section data-testid="why-daakyi-section" className="bg-white py-20 lg:py-28 border-b border-[#DCE4EE]">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
        <div className="max-w-[640px]">
          <p className="text-[#A8631A] text-xs font-semibold uppercase tracking-widest mb-4">Why DAAKYI Cloud</p>
          <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-[#1A1240]">
            Infrastructure you can question, inspect and hold accountable.
          </h2>
        </div>
        <Link to="/why-daakyi" data-testid="home-why-daakyi-link" className="text-[#A8631A] hover:text-[#DD8226] text-sm font-semibold inline-flex items-center gap-1.5 shrink-0 transition-colors">
          Why DAAKYI Cloud <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {WHY_PILLARS.map(({ icon: Icon, title, desc }) => (
          <div key={title} className="border-t-2 border-[#F29B3D] pt-5">
            <Icon className="w-5 h-5 text-[#A8631A] mb-3" strokeWidth={1.5} />
            <h3 className="text-[#1A1240] font-semibold text-[15px] mb-2">{title}</h3>
            <p className="text-[#64748B] text-sm leading-relaxed">{desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ── Region Proof ── */
export const RegionProofSection = () => (
  <section data-testid="region-proof-section" className="bg-[#F8FAFC] py-16 lg:py-20">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <div className="bg-white border border-[#DCE4EE] rounded-xl p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-8">
          <p className="text-[#A8631A] text-xs font-semibold uppercase tracking-widest mb-3">Cloud Infrastructure</p>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight text-[#1A1240]">Flagship region — live today</h2>
            <span data-testid="home-region-status" className="inline-flex items-center gap-1.5 text-[#F29B3D] text-xs font-bold uppercase tracking-wider bg-[#F29B3D]/[0.08] border border-[#F29B3D]/25 rounded-full px-3 py-1">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F29B3D]" /> Available
            </span>
          </div>
          <p className="text-[#64748B] text-[15px] leading-relaxed max-w-xl">
            Explore available compute, storage, networking, security, backup and managed cloud
            services in DAAKYI&rsquo;s production region — operated by our own engineers.
          </p>
        </div>
        <div className="lg:col-span-4 flex lg:justify-end">
          <Link to="/regions" data-testid="home-explore-regions" className="btn-primary inline-flex items-center gap-2">
            Explore Regions <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

/* ── Trust CTA ── */
export const TrustCTASection = () => (
  <section data-testid="trust-cta-home-section" className="bg-white py-16 lg:py-20 border-t border-[#DCE4EE]">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-8">
          <p className="text-[#F29B3D] text-xs font-semibold uppercase tracking-widest mb-3">Trust Center</p>
          <h2 className="text-2xl lg:text-3xl font-extrabold tracking-tight text-[#1A1240] mb-3">
            Built for workloads that require trust.
          </h2>
          <p className="text-[#64748B] text-[15px] leading-relaxed max-w-xl">
            Explore DAAKYI Cloud&rsquo;s approach to infrastructure security, tenant isolation,
            data protection, workload security and operational resilience.
          </p>
        </div>
        <div className="lg:col-span-4 flex lg:justify-end">
          <Link to="/trust" data-testid="home-visit-trust" className="btn-glass inline-flex items-center gap-2">
            Visit the Trust Center <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);

/* ── Final CTA ── */
export const CTASection = () => (
  <section data-testid="cta-section" className="section-dark relative py-24 lg:py-32 overflow-hidden">
    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[70%] h-[1px] bg-gradient-to-r from-transparent via-[#F29B3D]/50 to-transparent" />
    <div className="max-w-[760px] mx-auto px-6 text-center relative z-10">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-6">
        Run what matters on DAAKYI Cloud.
      </h2>
      <p className="text-[#93A5BC] text-base lg:text-lg mb-8">
        Tell us about your workloads, or talk through your architecture with an engineer first.
      </p>
      <div className="flex items-center justify-center gap-3 mb-10" data-testid="cta-engineer-note">
        <img src={PHOTOS.engineerPortrait} alt="DAAKYI cloud engineer" loading="lazy"
          className="w-11 h-11 rounded-full object-cover border-2 border-white/20" />
        <p className="text-[#93A5BC] text-sm text-left">An engineer joins every conversation — no sales scripts.</p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-4">
        <Link to="/cloud-access" data-testid="cta-request-access"
          className="bg-[#F29B3D] hover:bg-[#DD8226] text-white font-semibold text-[15px] px-8 py-3.5 rounded-[10px] transition-colors duration-200">
          Request Cloud Access
        </Link>
        <Link to="/contact" data-testid="cta-talk-architect"
          className="border border-white/20 hover:border-[#F29B3D]/60 text-white font-medium text-[15px] px-8 py-3.5 rounded-[10px] transition-colors duration-200">
          Talk to a Cloud Architect
        </Link>
      </div>
    </div>
  </section>
);
